/**
 * Quick Add drawer.
 *
 * New, self-contained feature: opt-in via settings.enable_quick_add_drawer.
 * Does not modify or depend on the existing Quick View / Quick Shop code
 * paths, only on stable/public theme building blocks:
 *  - the generic drawer engine (`[data-drawer-options]`, already used by the
 *    mini cart) to open/close #t4s-quick-add-drawer
 *  - Shopify's Section Rendering API to fetch `sections/quick-add.liquid`
 *    for a given product
 *  - `T4SThemeSP.Product`, the theme's own public product-form controller,
 *    to wire up swatches / quantity / AJAX add to cart inside the fetched
 *    markup, exactly like it does for Quick Shop.
 *
 * Safe to copy as-is into other stores running this theme.
 */
(function () {
  'use strict';

  var DRAWER_ID = 't4s-quick-add-drawer';
  var SECTION_ID = 'quick-add';
  var cache = {};
  var heightHackObserver = null;

  function getBody() {
    var drawer = document.getElementById(DRAWER_ID);
    return drawer ? drawer.querySelector('[data-quick-add-body]') : null;
  }

  function loadingMarkup() {
    return (
      '<div class="t4s-quick-add-loading">' +
      '<svg viewBox="0 0 66 66" width="40" height="40" aria-hidden="true" focusable="false">' +
      '<circle fill="none" stroke-width="4" stroke="currentColor" cx="33" cy="33" r="28"></circle>' +
      '</svg></div>'
    );
  }

  function errorMarkup() {
    return '<p class="t4s-quick-add-error">Unable to load this product right now.</p>';
  }

  function moveWishlistIntoFooter(panel) {
    var wishlistBtn = panel.querySelector('[data-quick-add-wishlist]');
    var buttonRow = panel.querySelector('.t4s-product-form__buttons');
    if (wishlistBtn && buttonRow) {
      buttonRow.appendChild(wishlistBtn);
    }
  }

  // T4SThemeSP.Product (reused from Quick Shop) is built for a real image
  // gallery layout; with no gallery in this drawer it can set an inline
  // height/spacing on these wrappers that leaves a large blank gap above the
  // options. CSS `!important` covers most cases; this covers any inline
  // `!important` the JS itself might set.
  var HEIGHT_HACK_SELECTOR =
    '[data-product-featured], [data-callbackvariant], .t4s-form__product, .t4s-product-form__variants, .t4s-swatch';

  function stripInlineHeightHacks(panel) {
    panel.querySelectorAll(HEIGHT_HACK_SELECTOR).forEach(function (el) {
      el.style.removeProperty('height');
      el.style.removeProperty('min-height');
      el.style.removeProperty('max-height');
      el.style.removeProperty('padding-top');
      el.style.removeProperty('margin-top');
    });
  }

  // Some theme JS applies this sizing asynchronously (on resize/image load),
  // so keep stripping it for as long as the current panel is in the drawer,
  // not just once. Disconnected/replaced each time a new panel is injected.
  function watchInlineHeightHacks(panel) {
    if (heightHackObserver) heightHackObserver.disconnect();
    heightHackObserver = new MutationObserver(function () {
      stripInlineHeightHacks(panel);
    });
    heightHackObserver.observe(panel, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true,
    });
  }

  function initInjectedProduct(panel) {
    var featured = panel.querySelector('[data-product-featured]');
    if (!featured) return;
    if (window.T4SThemeSP && typeof T4SThemeSP.Product === 'function') {
      try {
        new T4SThemeSP.Product(featured);
      } catch (err) {
        /* noop: markup still renders/links to the PDP even if JS init fails */
      }
      stripInlineHeightHacks(panel);
      watchInlineHeightHacks(panel);
    }
    moveWishlistIntoFooter(panel);
    if (window.T4SThemeSP && T4SThemeSP.Tooltip) {
      try { T4SThemeSP.Tooltip(); } catch (err) {}
    }
    if (window.jQuery && window.T4SThemeSP && T4SThemeSP.Wishlist && T4SThemeSP.Wishlist.updateAll) {
      try { T4SThemeSP.Wishlist.updateAll(); } catch (err) {}
    }
  }

  function renderProduct(body, url) {
    body.innerHTML = loadingMarkup();

    var fetchUrl = url.split('?')[0] + '?section_id=' + SECTION_ID;

    var request = cache[fetchUrl]
      ? Promise.resolve(cache[fetchUrl])
      : fetch(fetchUrl, { credentials: 'same-origin' })
          .then(function (res) {
            if (!res.ok) throw new Error('quick-add: request failed');
            return res.text();
          })
          .then(function (html) {
            cache[fetchUrl] = html;
            return html;
          });

    request
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var panel = doc.querySelector('[data-quick-add-panel]');
        if (!panel) throw new Error('quick-add: panel not found in response');
        body.innerHTML = '';
        body.appendChild(panel);
        initInjectedProduct(panel);
        document.dispatchEvent(new CustomEvent('quick-add:rendered', { detail: { url: url } }));
      })
      .catch(function () {
        body.innerHTML = errorMarkup();
      });
  }

  // Back in stock popup. Klaviyo's own modal is built around the page's single product (on a
  // product page its trigger always opens the page's product, whatever the drawer shows), so
  // inside the drawer the trigger opens a copy of that modal for the drawer's product instead.
  // It posts exactly like Klaviyo's onsite.js does (same endpoints, headers and variant id format).
  var BIS_API = 'https://a.klaviyo.com/client/';

  function closeBis() {
    var modal = document.querySelector('.t4s-quick-add-bis-modal');
    if (modal) modal.remove();
    document.removeEventListener('keydown', onBisKey);
  }

  function onBisKey(e) {
    if (e.key === 'Escape') closeBis();
  }

  function bisPost(path, account, body) {
    return fetch(BIS_API + path + '/?company_id=' + encodeURIComponent(account), {
      method: 'POST',
      headers: { revision: '2024-02-15', 'Content-Type': 'application/json', 'X-Klaviyo-Onsite': '1' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('klaviyo: ' + res.status);
    });
  }

  function openBis(panel) {
    var dataEl = panel.querySelector('[data-quick-add-bis]');
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }
    if (!data.variants.length) return;

    var variantField = panel.querySelector('[name="id"]');
    var selectedId = variantField && variantField.value;

    closeBis();
    var modal = document.createElement('div');
    modal.className = 't4s-quick-add-bis-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');

    var inner = document.createElement('div');
    inner.className = 't4s-quick-add-bis-modal__inner';

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 't4s-quick-add-bis-modal__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&#10005;';

    var title = document.createElement('h2');
    title.textContent = data.title;

    var intro = document.createElement('p');
    intro.textContent = 'Register to receive a notification when this item comes back in stock.';

    var form = document.createElement('form');
    form.noValidate = true;

    var select = document.createElement('select');
    select.setAttribute('aria-label', 'Variant');
    data.variants.forEach(function (v) {
      var opt = document.createElement('option');
      opt.value = v.id;
      opt.textContent = v.title;
      if (String(v.id) === String(selectedId)) opt.selected = true;
      select.appendChild(opt);
    });
    if (data.variants.length === 1) select.hidden = true;

    var email = document.createElement('input');
    email.type = 'email';
    email.placeholder = 'Email';
    email.autocomplete = 'email';
    email.setAttribute('aria-label', 'Email');

    var newsletterLabel = document.createElement('label');
    newsletterLabel.className = 't4s-quick-add-bis-modal__newsletter';
    var newsletter = document.createElement('input');
    newsletter.type = 'checkbox';
    newsletter.checked = true;
    newsletterLabel.appendChild(newsletter);
    newsletterLabel.appendChild(document.createTextNode(
      ' Also subscribe me to EXCLUSIVE! access to our hottest offers, new product launches, unmissable events and more!'
    ));
    if (!data.list) newsletterLabel.hidden = true;

    var submit = document.createElement('button');
    submit.type = 'submit';
    submit.textContent = 'Notify me when available';

    var msg = document.createElement('p');
    msg.className = 't4s-quick-add-bis-modal__msg';
    msg.setAttribute('role', 'status');

    form.appendChild(select);
    form.appendChild(email);
    form.appendChild(newsletterLabel);
    form.appendChild(submit);
    form.appendChild(msg);

    inner.appendChild(close);
    inner.appendChild(title);
    inner.appendChild(intro);
    inner.appendChild(form);
    modal.appendChild(inner);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var address = email.value.trim();
      msg.className = 't4s-quick-add-bis-modal__msg';
      if (!/@/.test(address)) {
        msg.textContent = 'Please enter a valid email address.';
        msg.classList.add('is-error');
        return;
      }
      submit.disabled = true;
      msg.textContent = '';
      var requests = [
        bisPost('back-in-stock-subscriptions', data.account, {
          data: {
            type: 'back-in-stock-subscription',
            attributes: { profile: { data: { type: 'profile', attributes: { email: address } } }, channels: ['EMAIL'] },
            relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + select.value } } }
          }
        })
      ];
      if (data.list && newsletter.checked) {
        requests.push(bisPost('subscriptions', data.account, {
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes: { email: address, properties: { $source: 'Back in Stock' } } } } },
            relationships: { list: { data: { type: 'list', id: data.list } } }
          }
        }));
      }
      Promise.all(requests)
        .then(function () {
          submit.hidden = true;
          msg.textContent = "You're in! We'll let you know when it's back.";
          msg.classList.add('is-success');
        })
        .catch(function () {
          submit.disabled = false;
          msg.textContent = 'Something went wrong. Please try again.';
          msg.classList.add('is-error');
        });
    });

    // Keep clicks from reaching the drawer's own outside-click handling.
    modal.addEventListener('click', function (e) {
      e.stopPropagation();
      if (e.target === modal || e.target === close) closeBis();
    });
    document.body.appendChild(modal);
    document.addEventListener('keydown', onBisKey);
    email.focus();
  }

  // Capture phase + stopImmediatePropagation so Klaviyo's own trigger handler never runs here.
  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest('.t4s-quick-add-panel .klaviyo-bis-trigger');
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openBis(trigger.closest('.t4s-quick-add-panel'));
    },
    true
  );

  // Size guide popup: a small self-contained lightbox (the theme's own image viewer needs
  // image dimensions, which this section doesn't have). Falls back to the link's new-tab
  // behaviour if anything goes wrong.
  function closeSizeGuide() {
    var modal = document.querySelector('.t4s-quick-add-sg-modal');
    if (modal) modal.remove();
    document.removeEventListener('keydown', onSizeGuideKey);
  }

  function onSizeGuideKey(e) {
    if (e.key === 'Escape') closeSizeGuide();
  }

  function openSizeGuide(src) {
    closeSizeGuide();
    var modal = document.createElement('div');
    modal.className = 't4s-quick-add-sg-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML =
      '<div class="t4s-quick-add-sg-modal__inner">' +
      '<button type="button" class="t4s-quick-add-sg-modal__close" aria-label="Close">' +
      '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
      '<line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" stroke-width="2" stroke-linecap="round"></line>' +
      '<line x1="20" y1="4" x2="4" y2="20" stroke="currentColor" stroke-width="2" stroke-linecap="round"></line>' +
      '</svg></button>' +
      '<img alt="Size guide"></div>';
    modal.querySelector('img').src = src;
    // Keep clicks from reaching the drawer's own outside-click handling.
    modal.addEventListener('click', function (e) {
      e.stopPropagation();
      if (e.target === modal || e.target.closest('.t4s-quick-add-sg-modal__close')) closeSizeGuide();
    });
    document.body.appendChild(modal);
    document.addEventListener('keydown', onSizeGuideKey);
  }

  document.addEventListener('click', function (e) {
    var guide = e.target.closest('[data-quick-add-size-guide]');
    if (guide) {
      e.preventDefault();
      openSizeGuide(guide.getAttribute('href'));
      return;
    }
    var trigger = e.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;
    var url = trigger.getAttribute('data-product-url');
    var body = getBody();
    if (!url || !body) return;
    renderProduct(body, url);
  });
})();
